import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

/**
 * Whose book the Hub is currently being read as.
 *
 * A carrier between pages, not a second source of truth. The URL wins on
 * arrival — that is what keeps deep links, a refresh and the back button
 * correct — and the store exists only because navigating from the sidebar
 * carries no query, which would otherwise drop the filter on every click.
 *
 * Deliberately NOT persisted to localStorage, unlike the sidebar's collapsed
 * state. That is a layout preference; this is a data scope, and one that
 * survived a browser restart would have somebody reading one broker's book
 * tomorrow morning and taking it for the whole agency.
 */
export const useBrokerScopeStore = defineStore('brokerScope', () => {
  // '' = every broker · 'none' = the unassigned · '<id>' = one broker
  const brokerId = ref('');

  /** Spread into a router link so a count lands on a list filtered the same way. */
  const query = computed(() => (brokerId.value ? { broker_id: brokerId.value } : {}));

  const isFiltered = computed(() => brokerId.value !== '');

  /**
   * Adopt whatever the URL says, on arrival at a page that supports the filter.
   * A page without `meta.brokerScope` neither reads nor writes it.
   */
  const adoptFromRoute = (route) => {
    if (!route?.meta?.brokerScope) return;

    const fromUrl = route.query?.broker_id;
    brokerId.value = fromUrl === undefined || fromUrl === null ? '' : String(fromUrl);
  };

  /**
   * `replace`, not `push`: the back button steps between pages, not between
   * states of a filter control.
   */
  const set = (value, router, route) => {
    brokerId.value = value === null || value === undefined ? '' : String(value);

    if (!router || !route?.meta?.brokerScope) return;

    const next = { ...route.query };
    if (brokerId.value) next.broker_id = brokerId.value;
    else delete next.broker_id;

    router.replace({ path: route.path, query: next });
  };

  const reset = (router, route) => set('', router, route);

  return { brokerId, query, isFiltered, adoptFromRoute, set, reset };
});
